import { auth, functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, runTransaction, serverTimestamp } from 'firebase/firestore';

/**
 * Trigger rendering of the Semble MDT Report document (Well Life Clinic).
 *
 * Calls the `renderSembleMdtReport` Cloud Function once the clinician has
 * approved every AI paragraph on the RD_SembleMDT review page.  The CF fills
 * the Semble Google Doc template with the approved paragraphs, the rules-based
 * BAARS-IV/AQ-10 questionnaire blocks, the verbatim approved CI Physical
 * Health + Risk paragraphs and the "To discuss in PAD" Plan lists, then stores
 * the DOCX/PDF under `documents.semblemdtreportReport`.
 *
 * Called from the review-page exit handler (PreAR-style: render fires when
 * fully approved and something changed this session) — never directly from
 * the dashboard.
 *
 * Uses `runTransaction` to atomically check whether a render/generation is
 * already processing before setting `sembleMDTReportProcessingStatus`,
 * preventing duplicate Cloud Function invocations from concurrent callers.
 * Fire-and-forget: returns once processing is set; the CF updates Firestore
 * on completion.
 */
export const TriggerRenderSembleMdtReport = async ({ clinicianId, patientId }) => {
  const renderSembleMdtReport = httpsCallable(functions, 'renderSembleMdtReport', {
    timeout: 360000, // 6 minutes — slightly above the server-side 5-min timeout
  });

  const user = auth.currentUser;
  if (!user || !user.uid) return { success: false, error: 'Not authenticated' };
  if (!clinicianId || !patientId) {
    console.error('Missing required parameters:', { clinicianId, patientId });
    return { success: false, error: 'Missing required parameters' };
  }

  try {
    const patientDocRef = doc(db, 'patients', patientId);

    // Transactional guard: only one caller wins the race to set 'processing'.
    let alreadyProcessing = false;
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(patientDocRef);
      const manageReports = snap.data()?.dataModel?.['6_manageReports'];
      if (manageReports?.sembleMDTReportProcessingStatus === 'processing') {
        alreadyProcessing = true;
        return;
      }
      transaction.update(patientDocRef, {
        'dataModel.6_manageReports.sembleMDTReportProcessingStatus': 'processing',
        'dataModel.6_manageReports.sembleMDTReportProcessingStartedAt': serverTimestamp(),
      });
    }, { maxAttempts: 10 });

    if (alreadyProcessing) {
      console.log('Skipping Semble MDT Report render — already processing');
      return { success: true, data: { status: 'already-processing' } };
    }

    // Fire-and-forget: invoke the CF without awaiting its result.
    renderSembleMdtReport({ clinicianId, patientId })
      .then((result) => console.log('Semble MDT Report rendered successfully:', result.data))
      .catch((error) => {
        console.error('Error in renderSembleMdtReport function:', error);
        // deadline-exceeded means the client timed out, but the CF is still
        // running server-side and will write its final status when done.
        if (error.code === 'deadline-exceeded') {
          console.log('Client-side timeout; function continues processing in background');
          return;
        }
        // FAILED_PRECONDITION means "paragraphs must be approved" — this is a
        // transient state (e.g. a concurrent regeneration cleared approvals),
        // not a hard failure.  Reset to 'complete' so the dashboard resolves
        // the row status from the approved/generated flags rather than showing
        // a misleading error state.
        if (error.code === 'failed-precondition') {
          console.log('Render rejected (paragraphs unapproved); resetting to complete');
          updateDoc(patientDocRef, {
            'dataModel.6_manageReports.sembleMDTReportProcessingStatus': 'complete',
          }).catch((updateError) => console.error('Error resetting processing status:', updateError));
          return;
        }
        updateDoc(patientDocRef, {
          'dataModel.6_manageReports.sembleMDTReportProcessingStatus': 'error',
        }).catch((updateError) => console.error('Error updating processing status:', updateError));
      });

    return { success: true, data: { status: 'processing' } };
  } catch (error) {
    console.error('Error in TriggerRenderSembleMdtReport transaction:', error);
    return { success: false, error };
  }
};
